// navigation — every destination in the app, declared once.
//
// ONE app, one route tree (docs/ONE_APP.md). There is no /field, /dispatch or
// /office prefix and no per-shell list: a person's destinations are a property
// of their ROLE, not of whichever shell they happen to be standing in. That
// mistake is what left an admin on a phone inside Dispatch with four tabs and
// no route to the rest of their own product, and a crew member inside a
// five-route app whose only escape was /classic.
//
// Adding a screen is one entry here. Nothing else needs to know.
//
// ── Two filters, and they are not the same one ──────────────────────────────
//
// `roles` is the COARSE filter — whether a destination appears at all. Screens
// still gate their own controls with useCan, because server/utils/permissions.js
// is the authority (role baseline ∪ per-user extra_permissions) and duplicating
// it here would give the client a second opinion that can disagree with the
// server. A destination listed for a role that lacks the finer permission shows
// a refusal on arrival rather than a broken link — deliberate.
//
// Where a route is guarded by a hard `checkRole(...)` rather than a permission,
// `roles` mirrors THAT list exactly, because extra_permissions cannot open such
// a door and offering the row would guarantee a 403. Those cases are commented
// individually; server/utils/__tests__/roleGateDivergence.test.js is the record
// of where the two models disagree.

import { TAB_LIMIT as LIMIT } from './tabPrefs';

/**
 * How many bottom tabs a phone gets, before More.
 *
 * Re-exported from tabPrefs.js, which owns the number and the reason: four,
 * down from five on 2026-09-05, because five plus More is six buttons on a
 * 390px screen and at 65px each the labels wrapped onto two and three lines.
 * Anyone can now change WHICH four (tabPrefs.js); this is how many fit.
 */
export { TAB_LIMIT } from './tabPrefs';

export const ROLES = ['worker', 'manager', 'accounting', 'admin'];
const EVERYONE = ROLES;
const ALL_STAFF = ['manager', 'accounting', 'admin'];
const DISPATCHERS = ['manager', 'admin'];
const ADMIN = ['admin'];

/**
 * Group labels, so a rename is one edit and GROUP_ORDER below cannot drift
 * from the table by a typo. `navigation.test.js` checks that every group named
 * in a destination is one of these and that every role's order names all of
 * them — a group missing from an order would fall silently to the bottom of
 * that role's sidebar, under You.
 */
export const GROUPS = {
  DAY: 'My day',
  DISPATCH: 'Dispatch',
  WORK: 'Work',
  MONEY: 'Money',
  RECORDS: 'Records',
  INSIGHT: 'Insight',
  ADMIN: 'Admin',
  YOU: 'You',
};

/**
 * Every destination, in menu order.
 *
 *   key      stable id — the active-row marker and the tab bar both use it
 *   path     the ONE url. No app prefix.
 *   group    sidebar heading — one of GROUPS above. WHERE that heading sits
 *            is not decided here: GROUP_ORDER, further down, orders them per
 *            ROLE, because the browser's menu order and the phone tab bar's
 *            order stopped being the same question on 2026-09-10
 *   roles    who sees the row at all, by role label. Additive only — see
 *            `needs` below, which can widen this and can never narrow it.
 *   needs    [resource, action] this row ALSO opens for. A person holding it
 *            sees the row even when their role is not in `roles`.
 *   short    what the TAB BAR calls it, where the full label will not fit in
 *            ~62px. Everywhere else still shows `label` — see barLabel().
 *   phone    true = a DEFAULT tab for the roles below, in this order
 *   phoneFor which of `roles` it is a default tab for; defaults to all of
 *            them. This is not the same question as `roles`: a manager can
 *            reach My hours and should not spend a tab on it, and Approvals
 *            is a crew lead's most-tapped screen and was two taps away
 *            behind More.
 *
 * ── `phone` is a DEFAULT, not a whitelist ───────────────────────────────────
 *
 * It was the whole answer until 2026-09-05: whatever this table said was on
 * your bar, permanently. Now it decides only what an unconfigured phone
 * starts with. Anyone can rearrange their own bar from More → Edit tabs, and
 * the editor offers EVERY destination their role can reach — so a bookkeeper
 * who lives in Estimates can put it on the bar even though nothing here marks
 * it `phone`, and `phone` never has to be a compromise between four jobs.
 *
 * The defaults still have to be a good guess, because most people will never
 * open the editor. See shared/tabPrefs.js for where a changed one is kept.
 */
export const DESTINATIONS = [
  // ── the day ───────────────────────────────────────────────────────────────
  //
  // These three carried `group: ''` — an UNGROUPED block, pinned to the top of
  // the sidebar because that is where a heading-less run of rows has to sit to
  // make sense. That was the whole reason they were ungrouped, and it is why
  // Overview could not be moved above them.
  //
  // The owner, 2026-09-10: "on browser, the work tab should be on top, with
  // overview on the very top." So the block had to move down, and a
  // heading-less block in the MIDDLE of a grouped sidebar reads as a rendering
  // fault. Named, it moves like any other group — and an admin's sidebar now
  // opens with a heading rather than with three loose rows.
  //
  // The visible side effect is the phone header's eyebrow: AppShell.js falls
  // back to the destination's group when a screen sets no chrome of its own,
  // so /today reads "MY DAY · Today" where it read "Today" alone. That is the
  // same shape every other screen already has ("WORK · Jobs", "MONEY ·
  // Invoices"), and the TITLE — the thing the 2026-09-05 fix was about — is
  // still the screen's own name.
  // A tab for the people who are ON a job. `phoneFor` since 2026-09-10, and it
  // names three of the four roles rather than defaulting to all of them,
  // because /field/today lists the jobs assigned to YOU and nobody assigns one
  // to the bookkeeper. Measured in a browser as a real `accounting` user with
  // four jobs scheduled that day: their first tab, and the screen the app
  // opened on, read "Nothing scheduled today · Check with dispatch if that
  // looks wrong." Still one tap away in More for anyone who wants it.
  { key: 'today', label: 'Today', icon: 'jobs', path: '/today', group: GROUPS.DAY, roles: EVERYONE, phone: true, phoneFor: ['worker', 'manager', 'admin'] },
  // A bar tab for the person whose hours they are. A manager reaches it
  // from More; spending one of their five tabs on it costs them Approvals.
  { key: 'hours', label: 'My hours', icon: 'clock', path: '/hours', group: GROUPS.DAY, roles: EVERYONE, phone: true, phoneFor: ['worker'] },

  // Reachable, deliberately. This screen was one of Field's three fixed tabs
  // and the first cut of this table left it with a route and no way in — a
  // crew member could not report a blocked job or a SAFETY problem at all.
  // Nothing linked to /problem; oneApp.test.js's "every route belongs to a
  // destination" is what found it.
  { key: 'problem', label: 'Report a problem', short: 'Problem', icon: 'alertTriangle', path: '/problem', group: GROUPS.DAY, roles: EVERYONE, phone: true, phoneFor: ['worker'] },
  // Time off. EVERYONE, because asking for a day is not a role — a manager and
  // a bookkeeper take holiday too, and the screen shows an approver their own
  // requests alongside the queue rather than sending them somewhere else for
  // it. NOT a phone tab: a crew member uses this a few times a year and
  // Approvals or Hours would be the tab it displaced.
  { key: 'time-off', label: 'Time off', icon: 'calendar', path: '/time-off', group: GROUPS.DAY, roles: EVERYONE },

  // ── dispatch: the endpoints behind these are requireDispatch, which is
  //    admin | manager | a timesheets permission.
  //
  // This comment used to end "a promoted lead reaches them by URL and the
  // screen answers, which is the honest side to err on." That was FALSE, and
  // measured so on 2026-09-07: AppRouter's `Allowed` reads the same
  // destinationsFor() the menu does, so a lead granted timesheets.approve got
  // "Not part of your app" from the URL as well as no row. Meanwhile the
  // server obeyed them — GET /dispatch/approve 200, /board 200, /now 200, and
  // PUT /timesheets/:id/approve actually approved the sheet.
  //
  // `needs` fixes it properly instead of erring: the row appears for whoever
  // the gate admits. Additive, so admin and manager are untouched.
  { key: 'board', label: 'Crew board', short: 'Board', icon: 'calendar', path: '/board', group: 'Dispatch', roles: DISPATCHERS, needs: ['timesheets', 'approve'], phone: true },
  // No `phone`, since 2026-09-05, and the ONLY reason is that the bar went
  // from five default tabs to four: Right now is a screen you glance at, and
  // Approvals is one you act on and that carries a badge, so when one of them
  // has to move to More it is this one. Anyone who disagrees puts it back —
  // the bar is arranged per person now (shared/tabPrefs.js), and the editor
  // offers every destination the role can reach, not just the defaults.
  { key: 'now', label: 'Right now', icon: 'activity', path: '/now', group: 'Dispatch', roles: DISPATCHERS, needs: ['timesheets', 'approve'] },
  { key: 'approve', label: 'Approvals', icon: 'check', path: '/approve', group: 'Dispatch', roles: DISPATCHERS, needs: ['timesheets', 'approve'], phone: true },

  // ── work ──────────────────────────────────────────────────────────────────
  { key: 'overview', label: 'Overview', icon: 'dashboard', path: '/overview', group: 'Work', roles: ALL_STAFF, phone: true },
  // `needs` here is the fix for "workers can't create jobs", 2026-09-07.
  //
  // Measured before changing anything: `worker` holds jobs:['create','read',
  // 'update'] in server/database.js, POST /api/jobs carries NO gate beyond
  // authMiddleware, and a real worker POSTing to it got 201 with user_id set
  // to themselves. GET /api/clients and /clients/properties/all both answer
  // 200 for them, so the form's pickers work. The permission said yes, the
  // server said yes, and this row said no — the menu was the only thing in
  // the way, and it was reading the role label.
  //
  // 'create' rather than 'read' deliberately: read would open the screen to
  // anyone who can see a job at all, which is a wider product change than the
  // one that was asked for. This opens it to the people who can make one.
  //
  // `phoneFor` since 2026-09-10 and it changes exactly one bar. Nobody else's
  // four tabs contain Jobs anyway — a manager's and an admin's are today,
  // board, approve, me, and a granted worker's are full before this row is
  // reached — so this only takes the slot back from `accounting`, who is
  // seeded `jobs: ['read']` and nothing else (server/database.js). A list they
  // may look at and not act on is not one of a bookkeeper's four most-tapped
  // screens; it is still in More.
  { key: 'jobs', label: 'Jobs', icon: 'jobs', path: '/jobs', group: 'Work', roles: ALL_STAFF, needs: ['jobs', 'create'], phone: true, phoneFor: ['manager', 'admin'] },
  // The same `needs` as the jobs row, and for the same reason: Schedule.js
  // gates New job and its seven per-day Add buttons on useCan('jobs','create'),
  // which is TRUE for a worker, and PATCH /api/jobs/:id/schedule answers 200
  // for them. Adding it to /jobs alone left the app's second New job button
  // behind a row they could not open (measured 2026-09-07).
  { key: 'schedule', label: 'Schedule', icon: 'calendar', path: '/schedule', group: 'Work', roles: ALL_STAFF, needs: ['jobs', 'create'] },

  // ── money ─────────────────────────────────────────────────────────────────
  // ALL_STAFF again as of 2026-09-05, and this is the round trip worth
  // understanding rather than a flip-flop.
  //
  // It was ALL_STAFF, and wrong: every route in invoices.js was
  // checkRole('admin','manager'), so the row put a bookkeeper on a screen that
  // answered 403 under two buttons that could only fail. It went to
  // DISPATCHERS to make the menu honest, with a note that whether the role
  // NAMED for bookkeeping should reach an invoice was a real question, and a
  // question about the SERVER — CLAUDE.md rule 4 puts that beyond a guess.
  //
  // The owner answered it: "bookkeeper should be able to see whatever he needs
  // to keep the books." So the four invoice READ routes now admit accounting
  // and the row comes back — pointing at a screen that works for them, which
  // is the only condition it was ever missing.
  //
  // The screen is READ-ONLY for that role, and by the same mechanism rather
  // than a second opinion: New invoice, From jobs, Send, Mark paid and the
  // Stripe sweep are each gated on their own route's `roles` in
  // shared/moneyEndpoints.js, and none of those was widened.
  //
  // `phone` for ACCOUNTING only, 2026-09-10. "What is owed and what is unpaid"
  // is the first question a bookkeeper opens this app with, and this row was
  // two taps away behind More while Today and a read-only Jobs list held two
  // of their four tabs. It is not a default for anyone else: a manager holds
  // no `invoices` permission at all and reaches this screen for the READ
  // routes only.
  { key: 'invoices', label: 'Invoices', icon: 'billing', path: '/invoices', group: 'Money', roles: ALL_STAFF, phone: true, phoneFor: ['accounting'] },
  // `needs` here makes the LEAD PROMOTION path in server/database.js:1065
  // actually work. Its own words:
  //
  //   "a worker can be promoted to 'lead' by an admin granting them
  //    estimates: ['read','create','update'] via the per-user
  //    extra_permissions mechanism. This is intentional — no new role needed
  //    … The lead can draft estimates"
  //
  // Measured 2026-09-07: grant those three and GET /api/estimates answers 200
  // for that worker, and the screen itself gates on useCan('estimates', …) —
  // so the whole feature worked except that this row was a role list and the
  // promoted lead had no way in. A documented feature nobody could use.
  //
  // 'read' rather than 'create': a worker holds NO estimates key at all by
  // default ("Intentionally absent" in the same file), so this opens the row
  // to exactly the people an admin has promoted and to nobody else. Drafting
  // is theirs; approve_send stays manager/admin through estimateWorkflow.js,
  // which this does not touch.
  { key: 'estimates', label: 'Estimates', icon: 'estimates', path: '/estimates', group: 'Money', roles: ALL_STAFF, needs: ['estimates', 'read'] },
  // The third of the bookkeeper's three, and the one they are seeded
  // `paystubs: [... 'manage_all']` for. A manager holds `paystubs: ['read']`
  // and an admin's four tabs are the dispatch ones, so this is a default for
  // accounting alone.
  { key: 'payroll', label: 'Payroll', icon: 'payroll', path: '/payroll', group: 'Money', roles: ALL_STAFF, phone: true, phoneFor: ['accounting'] },
  // Money in & out (docs/MONEY_TAXES.md, owner 2026-09-26: "a money tab for
  // accounting to see money coming and going"). GET /api/office/money answers
  // to financials.read — admin and accounting by default — and `needs` opens
  // the row to anyone granted it per user, exactly as the route does.
  { key: 'money', label: 'Money in & out', short: 'Money', icon: 'dollar', path: '/money', group: 'Money', roles: ['accounting', 'admin'], needs: ['financials', 'read'] },
  // Expenses (docs/MONEY_TAXES.md Phase 5): the other "out" of Money in &
  // out. GET /api/office/expenses is financials.read, like Money in & out;
  // adding, changing or deleting one also needs financials.export, which the
  // screen checks for its own buttons.
  { key: 'expenses', label: 'Expenses', icon: 'briefcase', path: '/expenses', group: 'Money', roles: ['accounting', 'admin'], needs: ['financials', 'read'] },
  // Sales tax (docs/MONEY_TAXES.md Phase 3). GET /api/office/taxes/sales is
  // financials.read, like Money in & out; recording a filing inside it also
  // needs financials.export, which the screen checks for its own buttons.
  { key: 'taxes', label: 'Sales tax', short: 'Tax', icon: 'fileText', path: '/taxes', group: 'Money', roles: ['accounting', 'admin'], needs: ['financials', 'read'] },
  // 1099s (docs/MONEY_TAXES.md Phase 4). GET /api/office/1099s is
  // workers.read_sensitive — what each worker was paid — so `needs` names it,
  // exactly as the route does.
  { key: 'nec', label: '1099s', icon: 'document', path: '/1099s', group: 'Money', roles: ['accounting', 'admin'], needs: ['workers', 'read_sensitive'] },

  // ── records ───────────────────────────────────────────────────────────────
  // Records. Each names the permission its own screen already gates its
  // controls on, so a per-user grant reaches the screen it was granted for.
  // Measured 2026-09-07: a worker granted clients.create got 201 from POST
  // /clients and POST /clients/properties, and "Not part of your app" from
  // both screens; one granted workers.read got 200 from GET /workers and the
  // same refusal at /crew.
  { key: 'clients', label: 'Clients', icon: 'clients', path: '/clients', group: 'Records', roles: ALL_STAFF, needs: ['clients', 'read'] },
  { key: 'properties', label: 'Properties', icon: 'home', path: '/properties', group: 'Records', roles: ALL_STAFF, needs: ['clients', 'read'] },
  { key: 'crew', label: 'Crew', icon: 'users', path: '/crew', group: 'Records', roles: ALL_STAFF, needs: ['workers', 'read'] },

  // ── insight ───────────────────────────────────────────────────────────────
  { key: 'reports', label: 'Reports', icon: 'reports', path: '/reports', group: 'Insight', roles: ALL_STAFF },
  // Everyone. server/routes/notifications.js gates reads on authMiddleware
  // alone, and a worker is told there when a job is assigned or paused —
  // the classic nav offered it to all four roles and the first cut of this
  // table quietly dropped workers, which is a notification nobody sees.
  { key: 'notifications', label: 'Notifications', short: 'Alerts', icon: 'notifications', path: '/notifications', group: 'Insight', roles: EVERYONE },
  { key: 'media', label: 'Media', icon: 'media', path: '/media', group: 'Insight', roles: DISPATCHERS },
  // ALL_STAFF as of 2026-09-05. All three /api/activity handlers used an
  // inline admin/manager test and refused accounting — even though
  // database.js has seeded that role `activity_log: ['read']` from the start,
  // so the gate contradicted the grant rather than qualifying it. The three
  // tests now admit accounting, because the audit log is how you answer "who
  // changed that", which is bookkeeping. A crew member is still refused: this
  // is every action every colleague has taken.
  // `needs` added 2026-09-09 with the permissions audit. routes/activity.js
  // GET / moved from checkRole(accounting, admin, manager) to
  // checkPermission('activity_log', 'read'). The seeded roles are the same
  // three, so nobody's menu changes — but the server now also obeys the grant
  // when an admin ticks "Activity log: read" for somebody else, and without
  // this row that person would be obeyed by the server and shown no way in.
  // navGateCoverage.test.js caught exactly that, which is what it is for.
  { key: 'activity', label: 'Activity log', short: 'Activity', icon: 'list', path: '/activity', group: 'Insight', roles: ALL_STAFF, needs: ['activity_log', 'read'] },

  // ── admin ─────────────────────────────────────────────────────────────────
  { key: 'settings', label: 'Settings', icon: 'gear', path: '/settings', group: 'Admin', roles: ADMIN },
  { key: 'users', label: 'Users', icon: 'workers', path: '/users', group: 'Admin', roles: ADMIN },
  // Reading or recording an SSN is checkRole('admin') on the server, and
  // every reveal is written to ssn_access_log with the reader's name. This
  // was the last thing only the classic Admin panel could do.
  { key: 'security', label: 'Security', icon: 'lock', path: '/security', group: 'Admin', roles: ADMIN },
  // The `roles` table. server/middleware/auth.js checkRole is a plain role
  // comparison with no extra_permissions escape, so a CUSTOM role passes
  // none of the 49 checkRole guards in server/routes — the screen says so.
  // All four endpoints are checkRole('admin').
  { key: 'roles', label: 'Roles', icon: 'users', path: '/roles', group: 'Admin', roles: ADMIN },
  // The email-log handler is checkRole('admin','manager') — managers yes,
  // accounting no. The one route in admin.js that shuts a bookkeeper out.
  { key: 'email-log', label: 'Email log', icon: 'mail', path: '/email-log', group: 'Admin', roles: DISPATCHERS },
  // Both status endpoints are admin-only inline checks in server.js.
  { key: 'system', label: 'System', icon: 'activity', path: '/system', group: 'Admin', roles: ADMIN },

  // ── everyone, always last ─────────────────────────────────────────────────
  { key: 'me', label: 'Me', icon: 'profile', path: '/me', group: 'You', roles: EVERYONE, phone: true },
];

/**
 * What a destination is called ON THE TAB BAR, which is the narrowest place
 * any label appears: a tab is 78px at 390px wide, less 4px of padding, so the
 * label gets **74px**.
 *
 * The full label is still what the sidebar and the More sheet show, because
 * neither is short of room and "Report a problem" tells a crew member what
 * the screen is for in a way "Problem" does not. This is a display detail of
 * one component, not a rename.
 *
 * ── Two rules, and the second is the one that was got wrong ─────────────────
 *
 * 1. A `short` exists only where the full label does NOT fit. That is a
 *    question about PIXELS, not characters, and guessing it produced both
 *    kinds of error at once. Measured in the browser, 2026-09-05:
 *
 *      Report a problem  104.1px   too wide — needs one
 *      Notifications      79.5px   too wide — needs one
 *      Crew board         71.2px   fits, but with 3px to spare
 *      Activity log       71.1px   same
 *      Properties         63.2px   FITS. "Sites" was never needed.
 *      Right now          61.5px   fits
 *      My hours           55.5px   fits
 *      Email log          56.1px   fits
 *
 *    So four of the eight came off. `Properties` is ten characters and fits,
 *    while `Notifications` is thirteen and does not — which is why the first
 *    version of the guard, a nine-character cap, was the wrong shape as well
 *    as the wrong number.
 *
 *    `Crew board` and `Activity log` keep theirs despite fitting: 3px of
 *    headroom is not headroom. The display face is loaded from Google Fonts
 *    and a device that falls back to its own sans renders wider, and the cost
 *    of being wrong is an ellipsis mid-word.
 *
 * 2. A `short` must be RECOGNISABLE as the thing it shortens — the same word,
 *    not a synonym. "Properties" showing as "Sites" is a second name to learn
 *    for no benefit. `Problem`, `Board` and `Activity` are all just the label
 *    with words removed.
 *
 *    `Alerts` is the one exception and is deliberate: nothing shorter than
 *    "Notifications" is a truncation of it, the icon is a bell, and the tab
 *    editor shows `shows as "Alerts"` when you pick the row. navigation.test.js
 *    holds both rules and names this exemption, so a second one has to be
 *    argued for rather than added.
 */
export function barLabel(d) {
  return (d && (d.short || d.label)) || '';
}

/**
 * Does this person hold `[resource, action]`?
 *
 * The same semantics as useCan in client/src/hooks/usePermission.js and
 * checkPermission in server/middleware/auth.js, and kept local so this module
 * stays importable without React: admin short-circuits, `manage_all` is the
 * only wildcard, and the marker actions (`manage_team`, `read_self`,
 * `read_all`, `read_sensitive`) are NOT wildcards.
 *
 * `permissions` is the already-merged set — role baseline union the user's
 * extra_permissions, which is what the server puts on req.user and what
 * /api/auth/me returns. This function does not compute it.
 */
function holds(permissions, role, need) {
  if (!Array.isArray(need) || need.length !== 2) return false;
  if (role === 'admin') return true;
  const held = (permissions || {})[need[0]];
  if (!Array.isArray(held)) return false;
  return held.includes(need[1]) || held.includes('manage_all');
}

/**
 * Everything this person may reach, in menu order.
 *
 * ── Why this takes permissions, and why it is ADDITIVE ─────────────────────
 *
 * The owner's rule is that permissions decide what somebody sees. This table
 * decided it by role label, which is how a worker who HOLDS jobs.create, and
 * whom the server would have obeyed, had no way to reach the screen that
 * creates one (see the `needs` note on the jobs row).
 *
 * A row is reachable if the role is listed OR the person holds the row's
 * `needs`. Never the other way round: `needs` can only ADD a row, never remove
 * one, so granting nothing to nobody leaves every role's menu byte-identical
 * to what it was. That is what makes this safe to ship in one go rather than
 * role by role — it cannot take a screen away from somebody mid-shift.
 *
 * `opts.permissions` is optional. Omitted, the behaviour is exactly the old
 * role-only filter, which is what keeps ~30 existing call sites and tests
 * honest rather than silently half-migrated.
 */
export function destinationsFor(role, opts = {}) {
  const perms = opts && opts.permissions;
  return DESTINATIONS.filter(
    (d) => d.roles.includes(role) || (d.needs && holds(perms, role, d.needs)),
  );
}

/**
 * The bottom tab bar for a phone: this role's first `phone: true` destinations.
 *
 * `me` is pinned last rather than taken in order, because a tab bar whose last
 * slot changes by role is one a person has to read every time.
 */
export function phoneTabsFor(role, limit = LIMIT, opts = {}) {
  const mine = destinationsFor(role, opts).filter((d) => d.phone && (!d.phoneFor || d.phoneFor.includes(role)));
  const me = mine.find((d) => d.key === 'me');
  const rest = mine.filter((d) => d.key !== 'me').slice(0, Math.max(0, limit - (me ? 1 : 0)));
  return me ? [...rest, me] : rest;
}

/** Everything NOT on the tab bar — the More sheet on a phone. */
export function moreFor(role, limit = LIMIT, opts = {}) {
  const tabs = new Set(phoneTabsFor(role, limit, opts).map((d) => d.key));
  return destinationsFor(role, opts).filter((d) => !tabs.has(d.key));
}

/**
 * What order the SIDEBAR puts the groups in, per role.
 *
 * ── Why this exists at all ─────────────────────────────────────────────────
 *
 * The sidebar took DESTINATIONS order, and so did the phone tab bar
 * (phoneTabsFor takes this table's first `phone: true` rows in order). One
 * array served both, so there was no way to change what a browser shows first
 * without changing what a thumb lands on — and the owner asked for exactly
 * that, 2026-09-10: "on browser, the work tab should be on top, with overview
 * on the very top. Let's make sure nav buttons are organized in a way that
 * make everyone efficient."
 *
 * Reordering DESTINATIONS to do it would have moved Overview and Jobs ahead of
 * Today on every phone, which turns a manager's bar from
 * today/board/approve/me into overview/jobs/today/me — the day they run,
 * replaced by two desk screens. So the two orders are separated here instead.
 * DESTINATIONS is unchanged and still owns the bar; this owns the menu.
 *
 * ── Why it is per ROLE ─────────────────────────────────────────────────────
 *
 * "Work first" is the instruction and it is not the whole instruction — a
 * bookkeeper's second heading should not be a dispatcher's. What each list
 * says, and why:
 *
 *   worker      My day first. They have no Overview and are never at a desk;
 *               everything below it only appears at all if an admin has
 *               granted them something (see `needs`), so the rest is the
 *               order those grants arrive in.
 *   manager     Work, then Dispatch. Overview on top per the instruction,
 *               then Jobs and Schedule — what they plan — then the crews they
 *               run. My day third because a manager is IN a yard and does
 *               clock in. Records before Money: a manager holds no `invoices`
 *               permission and reaches that screen for the read routes only,
 *               while Clients, Properties and Crew are daily.
 *   accounting  Work, then Money. Overview on top — the same session that
 *               moved this role's landing page there measured 22 populated
 *               figures on it for them and zero withheld. Then the three
 *               screens they are seeded manage_all for. My day near the
 *               bottom, because nobody assigns a job to the bookkeeper:
 *               /today reads "Nothing scheduled today" for them, measured.
 *   admin       Work, Dispatch, Money, Records, Insight, then their own day,
 *               then Admin. Settings, Users, Security and Roles are
 *               occasional and the most destructive rows in the product; they
 *               do not belong under a person's thumb on the way to anything.
 *
 * Every list names EVERY group, and navigation.test.js enforces that. A group
 * left out of one role's list would fall to the bottom of that role's sidebar,
 * under You — which is exactly what a granted worker reaching Work would have
 * hit if this only listed the groups each role sees today.
 */
const G = GROUPS;
export const GROUP_ORDER = {
  worker: [G.DAY, G.WORK, G.DISPATCH, G.MONEY, G.RECORDS, G.INSIGHT, G.ADMIN, G.YOU],
  manager: [G.WORK, G.DISPATCH, G.DAY, G.RECORDS, G.MONEY, G.INSIGHT, G.ADMIN, G.YOU],
  accounting: [G.WORK, G.MONEY, G.RECORDS, G.INSIGHT, G.DAY, G.DISPATCH, G.ADMIN, G.YOU],
  admin: [G.WORK, G.DISPATCH, G.MONEY, G.RECORDS, G.INSIGHT, G.DAY, G.ADMIN, G.YOU],
};

/**
 * The order for a role this table does not name.
 *
 * server/routes/roles.js lets an admin define a CUSTOM role, and such a person
 * passes none of the checkRole guards — but `needs` can still open rows for
 * them, so they can have a menu. Admin's order is the general-purpose one and
 * is what they get. Never DESTINATIONS order: that would put the day above
 * Work for one role and below it for the other four, which is the sort of
 * inconsistency nobody can see from inside one session.
 */
const DEFAULT_GROUP_ORDER = GROUP_ORDER.admin;

/**
 * The sidebar shape: [{ label, items: [{ key, label, icon, path }] }].
 *
 * Rows keep DESTINATIONS order WITHIN a group — that is still the one place
 * "Overview, then Jobs, then Schedule" is written down — and the GROUPS are
 * ordered by the role's list above. A group with no rows this person can reach
 * is omitted rather than rendered empty.
 */
export function navGroupsFor(role, opts = {}) {
  const byLabel = new Map();
  for (const d of destinationsFor(role, opts)) {
    if (!byLabel.has(d.group)) byLabel.set(d.group, { label: d.group, items: [] });
    byLabel.get(d.group).items.push({ key: d.key, label: d.label, icon: d.icon, path: d.path });
  }
  const order = GROUP_ORDER[role] || DEFAULT_GROUP_ORDER;
  const out = order.map((label) => byLabel.get(label)).filter(Boolean);
  // Anything the order does not name still ships, at the end, rather than
  // disappearing: a destination is never lost to a typo in a label. The test
  // suite refuses the typo separately, which is where that belongs — losing a
  // whole group from a live sidebar is not a thing to discover in production.
  for (const [label, group] of byLabel) if (!order.includes(label)) out.push(group);
  return out;
}

/**
 * How many nav ROWS a sidebar opens with, before the remaining groups start
 * collapsed to their heading.
 *
 * ── The measurement this number comes from ─────────────────────────────────
 *
 * Chromium, the built app, 1440x900, one full-height sidebar per role, read
 * off the live DOM on 2026-09-12 with every group expanded — which is what
 * the sidebar did until this change:
 *
 *   admin       26 rows, 8 headings   1495px of content in 844px   16/26 visible
 *   manager     21 rows, 8 headings   1276px in 844px              15/21 visible
 *   accounting  16 rows, 6 headings   1015px in 844px              15/16 visible
 *   worker       8 rows, 5 headings    844px in 844px               8/8  visible
 *
 * So an admin had TEN rows below the fold — their own day, all six Admin
 * rows, and Me — and every one of the 26 was the same 40px row as every
 * other. Twenty-six equal rows is not a menu, it is a list to read.
 *
 * The budget is in ROWS rather than pixels on purpose: a pixel budget would
 * make the menu depend on the window it is measured in, so two people at two
 * screen sizes would see different groups open and neither could be told what
 * the rule was. Rows are a property of the TABLE, so the answer is the same
 * everywhere and a test can state it.
 *
 * Nine, derived from the worst case rather than picked. The scroll region is
 * the viewport less the pinned brand (900 - 56 = 844), the signed-in block at
 * its foot costs ~104, and a sidebar with the most headings any role has
 * (eight) spends 8x32 on headings, 7x12 on the gaps between groups and 16 on
 * its own padding — 356. That leaves (844 - 104 - 356) / 40 = 9.6 rows, and
 * 9 is the whole number under it.
 *
 * A number chosen from the worst case is right for the better ones too: at
 * nine, a worker's whole 8-row menu stays open and nothing is collapsed for
 * somebody who had no crowding problem.
 */
export const OPEN_ROW_BUDGET = 9;

/**
 * Which groups a person's sidebar OPENS with. The rest start collapsed — one
 * heading row each, still labelled, still one click from their rows.
 *
 * The open set is a PREFIX of that role's own GROUP_ORDER, and that is the
 * whole rule. No second table, and deliberately not a new taxonomy: the order
 * above is already this repo's per-role statement of what a role lives in —
 * "Work, then Dispatch" for a manager, "Work, then Money" for a bookkeeper —
 * argued destination by destination where it is declared. Reading it as "and
 * the top of that order is what opens" adds no new opinion to maintain.
 *
 * A prefix rather than a fitting: taking groups until one does not fit and
 * then carrying on would leave an open group UNDER a collapsed one, which
 * reads as a rendering fault rather than as a rule. So the walk stops at the
 * first group that would not fit.
 *
 * The first group always opens even if it alone is over budget, because a
 * sidebar of nothing but headings is not a menu either.
 *
 * This is a DEFAULT and not a whitelist, the same way `phone` is on the table
 * above: the shell opens whichever group holds the screen you are on, and
 * remembers the ones a person opens or closes themselves
 * (apps/shell/navGroups.js). Nothing here is ever the only way to see a row.
 */
export function defaultOpenGroupsFor(role, opts = {}) {
  return openGroupPrefix(navGroupsFor(role, opts));
}

/**
 * The arithmetic behind the rule above, over groups handed in directly.
 *
 * Separated from `defaultOpenGroupsFor` for one reason, and it is CLAUDE.md
 * rule 8 rather than tidiness: with TODAY's table, `break` and `continue` here
 * produce identical answers for all four roles, because every role's walk
 * happens to stop with `rows` sitting exactly on the budget and nothing after
 * it fits either. So a prefix assertion driven off the real roles cannot see
 * the difference — it passes, proves nothing, and would keep passing the day
 * a group changed size and an open heading appeared under a closed one.
 *
 * Measured, not assumed: replacing `break` with `continue` produced ZERO
 * failures across the navigation, navGroups, DesktopShell and AppShell suites,
 * and so did removing the `open.length &&` guard. Both are load-bearing now
 * because this function can be handed groups the roles do not currently make.
 */
export function openGroupPrefix(groups, budget = OPEN_ROW_BUDGET) {
  const open = [];
  let rows = 0;
  for (const g of (groups || [])) {
    const n = ((g && g.items) || []).length;
    // `break`, never `continue`: a smaller group opening after a larger one
    // was skipped would leave an open heading UNDER a closed one, which has no
    // rule a person could infer and reads as a rendering fault.
    if (open.length && rows + n > budget) break;
    // The first group opens whatever it costs. A sidebar of nothing but
    // headings is not a menu either.
    open.push(g.label);
    rows += n;
  }
  return open;
}

/** The path a nav key points at. */
export function pathForKey(key) {
  const d = DESTINATIONS.find((x) => x.key === key);
  return d ? d.path : '/';
}

/**
 * Which nav key a pathname belongs to, for the active row.
 *
 * Longest match wins, so /today/job/12 marks Today and /approve/7 marks
 * Approvals rather than falling through to the first prefix that happens to fit.
 */
export function keyForPath(pathname) {
  const p = String(pathname || '/');
  let best = null;
  for (const d of DESTINATIONS) {
    if (p === d.path || p.startsWith(`${d.path}/`)) {
      if (!best || d.path.length > best.path.length) best = d;
    }
  }
  return best ? best.key : null;
}

/**
 * Where this role lands on `/`.
 *
 * A crew member starts on their day. Everyone else starts on the numbers —
 * except that a phone-sized manager also starts on the day, because the first
 * thing a manager does on a phone in a yard is the same thing a crew member
 * does, and Overview is a desktop screen wearing a phone layout.
 *
 * ── The one role that is never in a yard ────────────────────────────────────
 *
 * `if (phone) return '/today'` applied to all four roles until 2026-09-10, and
 * the reasoning above only ever covered the manager. A bookkeeper is never
 * ASSIGNED a job, so /field/today has nothing of theirs to list — measured in
 * a browser at 320px and 390px as a real `accounting` user, with four jobs
 * scheduled for that day in the database:
 *
 *   Today · Nothing scheduled today · Check with dispatch if that looks wrong.
 *   THIS WEEK  M0 T0 W0 T0 F0 S0 S0 · 0.00 hours this week
 *
 * That is the whole screen. Nothing owed, nothing unpaid, nothing waiting —
 * and their tab bar already carried Overview in the second slot, so the app
 * knew where they belonged and opened somewhere else anyway.
 *
 * Overview is where they belong at BOTH widths, and it is not a desk screen
 * for this role the way it is for a manager: the same session measured 22
 * populated figures on it as accounting and zero withheld ones (they hold
 * `financials: ['read','export']`, server/database.js), with no horizontal
 * scroll in any container at 320px.
 *
 * Expressed as a permission rather than a role label, so a custom role that
 * cannot clock in is treated the same way: `time_entries.create` is what
 * putting yourself on a job needs (server/routes/timeEntries.js), and
 * server/database.js seeds it to worker, manager and admin but not to
 * accounting. Falls back to the role label when no permission set was passed,
 * because ~30 call sites and tests still call this without one.
 */
function clocksIn(role, permissions) {
  if (role === 'admin' || role === 'worker' || role === 'manager') return true;
  return holds(permissions, role, ['time_entries', 'create']);
}

export function homePathFor(role, { phone = false, permissions = null } = {}) {
  // Unchanged for a worker even when a grant widens their menu: /today is the
  // screen their day starts on, and landing them somewhere else because an
  // admin ticked a box would be a surprise, not a feature.
  if (role === 'worker') return '/today';
  const home = destinationsFor(role, { permissions }).some((d) => d.key === 'overview') ? '/overview' : '/today';
  if (phone && clocksIn(role, permissions)) return '/today';
  return home;
}
